/**
 * BeezFy Featured Collections - Premium JavaScript
 * Advanced animations, parallax effects, and interactive features
 */

(function() {
  'use strict';

  class BeezFyCollections {
    constructor() {
      this.init();
    }

    init() {
      this.initAnimations();
      this.initParallax();
      this.initCarousels();
      this.initAnalytics();
      this.initAccessibility();
    }

    /**
     * Initialize scroll-triggered animations
     */
    initAnimations() {
      const animatedElements = document.querySelectorAll('.beezfy-animate');
      
      if (!animatedElements.length) return;

      const observerOptions = {
        threshold: 0.1,
        rootMargin: '50px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const delay = entry.target.dataset.animationDelay || 0;
            setTimeout(() => {
              entry.target.classList.add('is-visible');
            }, delay);
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      animatedElements.forEach(el => observer.observe(el));
    }

    /**
     * Initialize parallax effects
     */
    initParallax() {
      const parallaxElements = document.querySelectorAll('.beezfy-animate--parallax');
      
      if (!parallaxElements.length) return;

      let ticking = false;

      const updateParallax = () => {
        parallaxElements.forEach(el => {
          const rect = el.getBoundingClientRect();
          const speed = el.dataset.parallaxSpeed || 0.5;
          const offset = (window.innerHeight - rect.top) * speed * 0.1;
          
          if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
            el.style.setProperty('--parallax-offset', `${offset}px`);
            el.classList.add('is-parallax');
          }
        });
        ticking = false;
      };

      const requestTick = () => {
        if (!ticking) {
          requestAnimationFrame(updateParallax);
          ticking = true;
        }
      };

      window.addEventListener('scroll', requestTick);
      window.addEventListener('resize', requestTick);
      requestTick();
    }

    /**
     * Initialize carousel functionality
     */
    initCarousels() {
      const carousels = document.querySelectorAll('.beezfy-collections-carousel');
      
      carousels.forEach(carousel => {
        new BeezFyCarousel(carousel);
      });
    }

    /**
     * Initialize analytics tracking
     */
    initAnalytics() {
      const section = document.querySelector('.beezfy-featured-collections');
      if (!section || !section.dataset.enableAnalytics) return;

      const cards = section.querySelectorAll('.beezfy-collection-card');
      const campaign = section.dataset.analyticsCampaign || 'featured-collections';

      cards.forEach((card, index) => {
        card.addEventListener('click', (e) => {
          const handle = card.dataset.collectionHandle;
          
          // Track with Google Analytics if available
          if (typeof gtag !== 'undefined') {
            gtag('event', 'select_collection', {
              collection_handle: handle,
              collection_position: index + 1,
              campaign: campaign
            });
          }

          // Track with Shopify analytics if available
          if (window.Shopify && window.Shopify.analytics) {
            window.Shopify.analytics.publish('collection_clicked', {
              collection_handle: handle,
              position: index + 1,
              campaign: campaign
            });
          }
        });
      });

      // Track impressions
      const trackImpression = () => {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'view_collection_list', {
            collection_count: cards.length,
            campaign: campaign
          });
        }
      };

      // Use Intersection Observer for impression tracking
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackImpression();
            observer.disconnect();
          }
        });
      }, { threshold: 0.5 });

      if (section) observer.observe(section);
    }

    /**
     * Initialize accessibility features
     */
    initAccessibility() {
      const section = document.querySelector('.beezfy-featured-collections');
      if (!section) return;

      // Enhanced keyboard navigation
      if (section.dataset.keyboardNavigation === 'true') {
        const cards = section.querySelectorAll('.beezfy-collection-card__link');
        
        cards.forEach((card, index) => {
          card.setAttribute('tabindex', '0');
          
          card.addEventListener('keydown', (e) => {
            let targetIndex = -1;
            
            switch(e.key) {
              case 'ArrowRight':
                targetIndex = index + 1;
                break;
              case 'ArrowLeft':
                targetIndex = index - 1;
                break;
              case 'ArrowDown':
                const cols = parseInt(getComputedStyle(section).getPropertyValue('--columns-desktop') || 3);
                targetIndex = index + cols;
                break;
              case 'ArrowUp':
                const colsUp = parseInt(getComputedStyle(section).getPropertyValue('--columns-desktop') || 3);
                targetIndex = index - colsUp;
                break;
            }
            
            if (targetIndex >= 0 && targetIndex < cards.length) {
              e.preventDefault();
              cards[targetIndex].focus();
            }
          });
        });
      }

      // Force high contrast if enabled
      if (section.dataset.forceContrast === 'true') {
        section.classList.add('beezfy-high-contrast');
      }

      // Announce dynamic content changes
      this.createAriaLiveRegion();
    }

    /**
     * Create ARIA live region for announcements
     */
    createAriaLiveRegion() {
      if (document.getElementById('beezfy-live-region')) return;
      
      const liveRegion = document.createElement('div');
      liveRegion.id = 'beezfy-live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      document.body.appendChild(liveRegion);
    }

    /**
     * Announce message to screen readers
     */
    announce(message) {
      const liveRegion = document.getElementById('beezfy-live-region');
      if (liveRegion) {
        liveRegion.textContent = message;
        setTimeout(() => {
          liveRegion.textContent = '';
        }, 1000);
      }
    }
  }

  /**
   * Carousel Class
   */
  class BeezFyCarousel {
    constructor(element) {
      this.carousel = element;
      this.track = element.querySelector('.beezfy-carousel__track');
      this.slides = element.querySelectorAll('.beezfy-carousel__slide');
      this.prevBtn = element.querySelector('[data-carousel-prev]');
      this.nextBtn = element.querySelector('[data-carousel-next]');
      this.dots = element.querySelectorAll('.beezfy-carousel__dot');
      
      this.currentSlide = 0;
      this.slidesPerView = parseInt(element.dataset.slidesDesktop || 3);
      this.autoplay = element.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(element.dataset.autoplaySpeed || 5000);
      this.loop = element.dataset.loop === 'true';
      this.pauseOnHover = element.dataset.pauseOnHover === 'true';
      
      this.init();
    }

    init() {
      if (!this.track || !this.slides.length) return;
      
      this.setupEventListeners();
      this.updateSlidesPerView();
      this.updateCarousel();
      
      if (this.autoplay) {
        this.startAutoplay();
      }
    }

    setupEventListeners() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }
      
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });
      
      // Touch/swipe support
      let touchStartX = 0;
      let touchEndX = 0;
      
      this.track.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      });
      
      this.track.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      });
      
      // Responsive updates
      window.addEventListener('resize', () => {
        this.updateSlidesPerView();
        this.updateCarousel();
      });
      
      // Pause on hover
      if (this.pauseOnHover && this.autoplay) {
        this.carousel.addEventListener('mouseenter', () => this.stopAutoplay());
        this.carousel.addEventListener('mouseleave', () => this.startAutoplay());
      }
    }

    updateSlidesPerView() {
      const width = window.innerWidth;

      if (width < 720) {
        this.slidesPerView = parseFloat(this.carousel.dataset.slidesMobile || 1);
      } else if (width < 1024) {
        this.slidesPerView = parseInt(this.carousel.dataset.slidesTablet || 2);
      } else {
        this.slidesPerView = parseInt(this.carousel.dataset.slidesDesktop || 3);
      }
    }

    updateCarousel() {
      const slideWidth = 100 / this.slidesPerView;
      const offset = -this.currentSlide * slideWidth;
      
      this.track.style.transform = `translateX(${offset}%)`;
      
      // Update dots
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === this.currentSlide);
      });
      
      // Update buttons
      if (!this.loop) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentSlide === 0;
        }
        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentSlide >= this.slides.length - this.slidesPerView;
        }
      }
    }

    next() {
      const maxSlide = this.slides.length - this.slidesPerView;
      
      if (this.currentSlide < maxSlide) {
        this.currentSlide++;
      } else if (this.loop) {
        this.currentSlide = 0;
      }
      
      this.updateCarousel();
      this.announce(`Slide ${this.currentSlide + 1} of ${this.slides.length}`);
    }

    prev() {
      if (this.currentSlide > 0) {
        this.currentSlide--;
      } else if (this.loop) {
        this.currentSlide = this.slides.length - this.slidesPerView;
      }
      
      this.updateCarousel();
      this.announce(`Slide ${this.currentSlide + 1} of ${this.slides.length}`);
    }

    goToSlide(index) {
      this.currentSlide = Math.min(index, this.slides.length - this.slidesPerView);
      this.updateCarousel();
      this.announce(`Slide ${this.currentSlide + 1} of ${this.slides.length}`);
    }

    handleSwipe(startX, endX) {
      const threshold = 50;
      const diff = startX - endX;
      
      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayInterval = setInterval(() => this.next(), this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
      }
    }

    announce(message) {
      const liveRegion = document.getElementById('beezfy-live-region');
      if (liveRegion) {
        liveRegion.textContent = message;
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new BeezFyCollections());
  } else {
    new BeezFyCollections();
  }

  // Re-initialize on Shopify section reload
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => new BeezFyCollections());
    document.addEventListener('shopify:section:reorder', () => new BeezFyCollections());
  }
})();